// src/context/MessagesContext.js
import React, { createContext, useContext, useState, useCallback } from 'react';
import { reportingAPI } from '../services/api';

const MessagesContext = createContext();

export const useMessages = () => {
  const context = useContext(MessagesContext);
  if (!context) {
    throw new Error('useMessages must be used within MessagesProvider');
  }
  return context;
};

export const MessagesProvider = ({ children }) => {
  const [messages, setMessages] = useState([]);
  const [totalMessages, setTotalMessages] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load messages with filters
  const loadMessages = useCallback(async (filters = {}) => {
    setLoading(true);
    try {
      const params = {
        limit: 20,
        ...filters,
      };

      const response = await reportingAPI.getHistory(params);
      setMessages(response.data.events || response.data.messages || []);
      setTotalMessages(response.data.total || 0);
      return response.data;
    } catch (error) {
      console.error('Error loading messages:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Load stats
  const loadStats = useCallback(async () => {
    try {
      const response = await reportingAPI.getStats();
      setStats(response.data);
      return response.data;
    } catch (error) {
      console.error('Error loading stats:', error);
      throw error;
    }
  }, []);

  // Optimistic update - update UI immediately, API in background
  const updateMessage = useCallback((messageId, updates) => {
    // Optimistically update UI
    setMessages(prev => 
      prev.map(msg => 
        msg.id === messageId 
          ? { ...msg, ...updates }
          : msg
      )
    );

    // Update stats if relevant
    if (updates.is_read !== undefined) {
      setStats(prev => prev ? {
        ...prev,
        unread_count: prev.unread_count + (updates.is_read ? -1 : 1)
      } : prev);
    }

    // Make API call in background (non-blocking)
    reportingAPI.updateMessageStatus(messageId, updates).catch(error => {
      console.error('Failed to update message:', error);
      // Revert on error
      loadMessages();
    });
  }, [loadMessages]);

  // Bulk update with optimistic UI
  const bulkUpdateMessages = useCallback(async (messageIds, updates) => {
    // Optimistically update UI
    setMessages(prev => 
      prev.map(msg => 
        messageIds.includes(msg.id)
          ? { ...msg, ...updates }
          : msg
      )
    );

    // Update stats if relevant
    if (updates.is_read !== undefined) {
      const count = messageIds.length;
      setStats(prev => prev ? {
        ...prev,
        unread_count: prev.unread_count + (updates.is_read ? -count : count)
      } : prev);
    }

    try {
      // Make API calls in parallel
      await Promise.all(
        messageIds.map(id => reportingAPI.updateMessageStatus(id, updates))
      );
    } catch (error) {
      console.error('Bulk update failed:', error);
      // Revert on error
      loadMessages();
      throw error;
    }
  }, [loadMessages]);

  // Add note (optimistic)
  const addNote = useCallback(async (messageId, noteText, author) => {
    // Create optimistic note
    const optimisticNote = {
      id: `temp-${Date.now()}`,
      note_text: noteText,
      created_at: new Date().toISOString(),
      author_name: author.name,
      author_email: author.email,
      edited: false,
    };

    try {
      const response = await reportingAPI.addNote(messageId, noteText);
      return response.data.note;
    } catch (error) {
      console.error('Failed to add note:', error);
      throw error;
    }
  }, []);

  const value = {
    messages,
    totalMessages,
    stats,
    loading,
    loadMessages,
    loadStats,
    updateMessage,
    bulkUpdateMessages,
    addNote,
    setMessages, // For manual updates if needed
    setStats,
  };

  return (
    <MessagesContext.Provider value={value}>
      {children}
    </MessagesContext.Provider>
  );
};
